import { handleActions } from 'redux-actions';
import type { ReducerAction } from '../types/reducer.types';
import {
  FETCH_USER_SIGNATURES_REQUEST,
  FETCH_USER_SIGNATURES_SUCCESS,
  FETCH_USER_SIGNATURES_FAILURE,
  CREATE_USER_SIGNATURE_REQUEST,
  CREATE_USER_SIGNATURE_SUCCESS,
  CREATE_USER_SIGNATURE_FAILURE,
  UPDATE_USER_SIGNATURE_REQUEST,
  UPDATE_USER_SIGNATURE_SUCCESS,
  UPDATE_USER_SIGNATURE_FAILURE,
  DELETE_USER_SIGNATURE_REQUEST,
  DELETE_USER_SIGNATURE_SUCCESS,
  DELETE_USER_SIGNATURE_FAILURE,
  SET_DEFAULT_USER_SIGNATURE_REQUEST,
  SET_DEFAULT_USER_SIGNATURE_SUCCESS,
  SET_DEFAULT_USER_SIGNATURE_FAILURE,
  FETCH_SYSTEM_SIGNATURES_REQUEST,
  FETCH_SYSTEM_SIGNATURES_SUCCESS,
  FETCH_SYSTEM_SIGNATURES_FAILURE,
  CREATE_SYSTEM_SIGNATURE_REQUEST,
  CREATE_SYSTEM_SIGNATURE_SUCCESS,
  CREATE_SYSTEM_SIGNATURE_FAILURE,
  UPDATE_SYSTEM_SIGNATURE_REQUEST,
  UPDATE_SYSTEM_SIGNATURE_SUCCESS,
  UPDATE_SYSTEM_SIGNATURE_FAILURE,
  DELETE_SYSTEM_SIGNATURE_REQUEST,
  DELETE_SYSTEM_SIGNATURE_SUCCESS,
  DELETE_SYSTEM_SIGNATURE_FAILURE,
  SET_DEFAULT_SYSTEM_SIGNATURE_REQUEST,
  SET_DEFAULT_SYSTEM_SIGNATURE_SUCCESS,
  SET_DEFAULT_SYSTEM_SIGNATURE_FAILURE,
  SET_SELECTED_SIGNATURE_ID,
  CLEAR_SIGNATURE_ERRORS,
} from '../actions/signatures.store.actions';

export interface UserSignature {
  id: string;
  name: string;
  content: string;
  isDefault: boolean;
  createdAt: string;
}

export interface SystemSignature {
  id: string;
  name: string;
  content: string;
  isDefault: boolean;
  isActive: boolean;
  email: string;
  firstName: string;
  lastName: string;
  createdAt: string;
}

interface SignatureState {
  // User signatures
  userSignatures: UserSignature[];
  userSignaturesLoading: boolean;
  userSignaturesError: string | null;

  // System signatures
  systemSignatures: SystemSignature[];
  systemSignaturesLoading: boolean;
  systemSignaturesError: string | null;

  // UI state
  selectedSignatureId: string | null;

  // Operation states
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  error: string | null;
}

const INITIAL_STATE: SignatureState = {
  userSignatures: [],
  userSignaturesLoading: false,
  userSignaturesError: null,

  systemSignatures: [],
  systemSignaturesLoading: false,
  systemSignaturesError: null,

  selectedSignatureId: null,

  creating: false,
  updating: false,
  deleting: false,
  error: null,
};

export const signaturesStore = handleActions(
  {
    // User signatures - fetch
    [FETCH_USER_SIGNATURES_REQUEST]: (state) => ({
      ...state,
      userSignaturesLoading: true,
      userSignaturesError: null,
    }),
    [FETCH_USER_SIGNATURES_SUCCESS]: (
      state,
      action: ReducerAction<UserSignature[]>
    ) => ({
      ...state,
      userSignatures: Array.isArray(action.payload) ? action.payload : [],
      userSignaturesLoading: false,
      userSignaturesError: null,
    }),
    [FETCH_USER_SIGNATURES_FAILURE]: (
      state,
      action: ReducerAction<string>
    ) => ({
      ...state,
      userSignaturesLoading: false,
      userSignaturesError: action.payload,
    }),

    // User signatures - create
    [CREATE_USER_SIGNATURE_REQUEST]: (state) => ({
      ...state,
      creating: true,
      error: null,
    }),
    [CREATE_USER_SIGNATURE_SUCCESS]: (
      state,
      action: ReducerAction<UserSignature>
    ) => ({
      ...state,
      userSignatures: Array.isArray(state.userSignatures)
        ? [...state.userSignatures, action.payload]
        : [action.payload],
      creating: false,
      error: null,
    }),
    [CREATE_USER_SIGNATURE_FAILURE]: (
      state,
      action: ReducerAction<string>
    ) => ({
      ...state,
      creating: false,
      error: action.payload,
    }),

    // User signatures - update
    [UPDATE_USER_SIGNATURE_REQUEST]: (state) => ({
      ...state,
      updating: true,
      error: null,
    }),
    [UPDATE_USER_SIGNATURE_SUCCESS]: (
      state,
      action: ReducerAction<UserSignature>
    ) => ({
      ...state,
      userSignatures: Array.isArray(state.userSignatures)
        ? state.userSignatures.map((sig) =>
            sig.id === action.payload.id ? action.payload : sig
          )
        : [action.payload],
      updating: false,
      error: null,
    }),
    [UPDATE_USER_SIGNATURE_FAILURE]: (
      state,
      action: ReducerAction<string>
    ) => ({
      ...state,
      updating: false,
      error: action.payload,
    }),

    // User signatures - delete
    [DELETE_USER_SIGNATURE_REQUEST]: (state) => ({
      ...state,
      deleting: true,
      error: null,
    }),
    [DELETE_USER_SIGNATURE_SUCCESS]: (
      state,
      action: ReducerAction<string>
    ) => ({
      ...state,
      userSignatures: Array.isArray(state.userSignatures)
        ? state.userSignatures.filter((sig) => sig.id !== action.payload)
        : [],
      deleting: false,
      error: null,
    }),
    [DELETE_USER_SIGNATURE_FAILURE]: (
      state,
      action: ReducerAction<string>
    ) => ({
      ...state,
      deleting: false,
      error: action.payload,
    }),

    // User signatures - set default
    [SET_DEFAULT_USER_SIGNATURE_REQUEST]: (state) => ({
      ...state,
      updating: true,
      error: null,
    }),
    [SET_DEFAULT_USER_SIGNATURE_SUCCESS]: (
      state,
      action: ReducerAction<string>
    ) => ({
      ...state,
      userSignatures: Array.isArray(state.userSignatures)
        ? state.userSignatures.map((sig) => ({
            ...sig,
            isDefault: sig.id === action.payload,
          }))
        : [],
      updating: false,
      error: null,
    }),
    [SET_DEFAULT_USER_SIGNATURE_FAILURE]: (
      state,
      action: ReducerAction<string>
    ) => ({
      ...state,
      updating: false,
      error: action.payload,
    }),

    // System signatures - fetch
    [FETCH_SYSTEM_SIGNATURES_REQUEST]: (state) => ({
      ...state,
      systemSignaturesLoading: true,
      systemSignaturesError: null,
    }),
    [FETCH_SYSTEM_SIGNATURES_SUCCESS]: (
      state,
      action: ReducerAction<SystemSignature[]>
    ) => ({
      ...state,
      systemSignatures: Array.isArray(action.payload) ? action.payload : [],
      systemSignaturesLoading: false,
      systemSignaturesError: null,
    }),
    [FETCH_SYSTEM_SIGNATURES_FAILURE]: (
      state,
      action: ReducerAction<string>
    ) => ({
      ...state,
      systemSignaturesLoading: false,
      systemSignaturesError: action.payload,
    }),

    // System signatures - create
    [CREATE_SYSTEM_SIGNATURE_REQUEST]: (state) => ({
      ...state,
      creating: true,
      error: null,
    }),
    [CREATE_SYSTEM_SIGNATURE_SUCCESS]: (
      state,
      action: ReducerAction<SystemSignature>
    ) => ({
      ...state,
      systemSignatures: Array.isArray(state.systemSignatures)
        ? [...state.systemSignatures, action.payload]
        : [action.payload],
      creating: false,
      error: null,
    }),
    [CREATE_SYSTEM_SIGNATURE_FAILURE]: (
      state,
      action: ReducerAction<string>
    ) => ({
      ...state,
      creating: false,
      error: action.payload,
    }),

    // System signatures - update
    [UPDATE_SYSTEM_SIGNATURE_REQUEST]: (state) => ({
      ...state,
      updating: true,
      error: null,
    }),
    [UPDATE_SYSTEM_SIGNATURE_SUCCESS]: (
      state,
      action: ReducerAction<SystemSignature>
    ) => ({
      ...state,
      systemSignatures: Array.isArray(state.systemSignatures)
        ? state.systemSignatures.map((sig) =>
            sig.id === action.payload.id ? action.payload : sig
          )
        : [action.payload],
      updating: false,
      error: null,
    }),
    [UPDATE_SYSTEM_SIGNATURE_FAILURE]: (
      state,
      action: ReducerAction<string>
    ) => ({
      ...state,
      updating: false,
      error: action.payload,
    }),

    // System signatures - delete
    [DELETE_SYSTEM_SIGNATURE_REQUEST]: (state) => ({
      ...state,
      deleting: true,
      error: null,
    }),
    [DELETE_SYSTEM_SIGNATURE_SUCCESS]: (
      state,
      action: ReducerAction<string>
    ) => ({
      ...state,
      systemSignatures: Array.isArray(state.systemSignatures)
        ? state.systemSignatures.filter((sig) => sig.id !== action.payload)
        : [],
      deleting: false,
      error: null,
    }),
    [DELETE_SYSTEM_SIGNATURE_FAILURE]: (
      state,
      action: ReducerAction<string>
    ) => ({
      ...state,
      deleting: false,
      error: action.payload,
    }),

    // System signatures - set default
    [SET_DEFAULT_SYSTEM_SIGNATURE_REQUEST]: (state) => ({
      ...state,
      updating: true,
      error: null,
    }),
    [SET_DEFAULT_SYSTEM_SIGNATURE_SUCCESS]: (
      state,
      action: ReducerAction<string>
    ) => ({
      ...state,
      systemSignatures: Array.isArray(state.systemSignatures)
        ? state.systemSignatures.map((sig) => ({
            ...sig,
            isDefault: sig.id === action.payload,
          }))
        : [],
      updating: false,
      error: null,
    }),
    [SET_DEFAULT_SYSTEM_SIGNATURE_FAILURE]: (
      state,
      action: ReducerAction<string>
    ) => ({
      ...state,
      updating: false,
      error: action.payload,
    }),

    // UI actions
    [SET_SELECTED_SIGNATURE_ID]: (
      state,
      action: ReducerAction<string | null>
    ) => ({
      ...state,
      selectedSignatureId: action.payload,
    }),
    [CLEAR_SIGNATURE_ERRORS]: (state) => ({
      ...state,
      error: null,
      userSignaturesError: null,
      systemSignaturesError: null,
    }),
  },
  INITIAL_STATE
);
